import React, { FC } from 'react'
import { styled } from '@linaria/react'
import { FormattedMessage } from 'react-intl'
import { useNavigate } from 'react-router'
import { Typography } from '../ui/Typography'
import { ButtonRegular } from '../ui/Button'
import { colors } from '../styles/colors'
import { DialogCloseButton } from './DialogCloseButton'
import { faqPagePath } from '../pages/FaqPage'
import QuestionIcon from '../svgr/Faq'
import VotingIcon from '../svgr/Vote'
import CheckMarkIcon from '../svgr/Check'

export const HowItWorksContent: FC<{
  onClose: () => void
}> = ({ onClose }) => {
  const navigate = useNavigate()
  return (
    <Container>
      <DialogCloseButton onClick={onClose} />

      <HeaderContainer>
        <StyledQuestionIcon />
        <Typography fontSize={20} fontWeight={700} color={colors.textPrimary}>
          <FormattedMessage
            id="newBox.howItWorks.title"
            defaultMessage="Как это работает?"
          />
        </Typography>
      </HeaderContainer>

      <StepsContainer>
        <Step>
          <StepIcon>
            <Typography fontSize={16} fontWeight={700} color={colors.white}>
              1
            </Typography>
          </StepIcon>
          <StepContent>
            <Typography
              fontSize={14}
              fontWeight={600}
              color={colors.textPrimary}
            >
              <FormattedMessage
                id="newBox.howItWorks.step1.title"
                defaultMessage="Создайте вопрос"
              />
            </Typography>
            <Typography
              fontSize={12}
              fontWeight={400}
              color={colors.textSecondary}
              font="roboto"
            >
              <FormattedMessage
                id="newBox.howItWorks.step1.description"
                defaultMessage="Придумайте интересный вопрос и варианты ответов для голосования"
              />
            </Typography>
          </StepContent>
        </Step>

        <Step>
          <StepIcon>
            <Typography fontSize={16} fontWeight={700} color={colors.white}>
              2
            </Typography>
          </StepIcon>
          <StepContent>
            <Typography
              fontSize={14}
              fontWeight={600}
              color={colors.textPrimary}
            >
              <FormattedMessage
                id="newBox.howItWorks.step2.title"
                defaultMessage="Установите параметры"
              />
            </Typography>
            <Typography
              fontSize={12}
              fontWeight={400}
              color={colors.textSecondary}
              font="roboto"
            >
              <FormattedMessage
                id="newBox.howItWorks.step2.description"
                defaultMessage="Выберите дедлайн, публичность бокса и добавьте изображение"
              />
            </Typography>
          </StepContent>
        </Step>

        <Step>
          <StepIcon>
            <StyledVotingIcon />
          </StepIcon>
          <StepContent>
            <Typography
              fontSize={14}
              fontWeight={600}
              color={colors.textPrimary}
            >
              <FormattedMessage
                id="newBox.howItWorks.step3.title"
                defaultMessage="Пользователи голосуют"
              />
            </Typography>
            <Typography
              fontSize={12}
              fontWeight={400}
              color={colors.textSecondary}
              font="roboto"
            >
              <FormattedMessage
                id="newBox.howItWorks.step3.description"
                defaultMessage="Делитесь боксом с друзьями и подписчиками, чтобы собрать голоса"
              />
            </Typography>
          </StepContent>
        </Step>

        <Step>
          <StepIcon>
            <StyledCheckMarkIcon />
          </StepIcon>
          <StepContent>
            <Typography
              fontSize={14}
              fontWeight={600}
              color={colors.textPrimary}
            >
              <FormattedMessage
                id="newBox.howItWorks.step4.title"
                defaultMessage="Получайте доход"
              />
            </Typography>
            <Typography
              fontSize={12}
              fontWeight={400}
              color={colors.textSecondary}
              font="roboto"
            >
              <FormattedMessage
                id="newBox.howItWorks.step4.description"
                defaultMessage="Вы получает комиссию с каждого голоса в вашем боксе. Вознаграждение автоматически зачисляется на ваш кошелек, когда бокс завершается"
              />
            </Typography>
          </StepContent>
        </Step>
      </StepsContainer>

      <MoreDetailsButton
        onClick={() => {
          onClose()
          navigate(`${faqPagePath}?section=howToCreateBox`)
        }}
        textColor={colors.white}
        backgroundColor={colors.secondary}
        textWeight={700}
      >
        <FormattedMessage
          id="newBox.howItWorks.moreDetails"
          defaultMessage="Больше деталей"
        />
      </MoreDetailsButton>
    </Container>
  )
}

const Container = styled.div`
  position: relative;
  display: grid;
  gap: 24px;
  background: ${colors.bgPrimary};
  border-radius: 20px 20px 0 0;
  padding: 24px;
  width: 100%;
`

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: center;
  text-align: center;
  margin-top: 8px;
`

const StepsContainer = styled.div`
  display: grid;
  gap: 16px;
`

const Step = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`

const StepIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${colors.secondary};
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
    color: ${colors.white};
  }
`

const StepContent = styled.div`
  display: grid;
  gap: 4px;
  flex: 1;
`

const MoreDetailsButton = styled(ButtonRegular)`
  width: 100%;
  margin-top: 8px;
`

const StyledQuestionIcon = styled(QuestionIcon)`
  width: 24px;
  height: 24px;
  color: ${colors.secondary};
`

const StyledVotingIcon = styled(VotingIcon)`
  width: 16px;
  height: 16px;
`

const StyledCheckMarkIcon = styled(CheckMarkIcon)`
  width: 16px;
  height: 16px;
`
